import type { Camera } from "./camera";
import { createBuffer, createVertexArray, describeAttributes } from "./gl/buffer";
import { GlError } from "./gl/context";
import { createProgram, uniformLocations, type Uniforms } from "./gl/shader";
import { sampleCurve, type Curve, type EffectManifest, type SpawnOptions } from "./particles";

/** One sub-mesh of one `.mde`, as `tools/build_effects.py` packed it: the atlas
 *  key of its diffuse map, its per-frame visibility, which geometry slot each
 *  animation frame draws, and each slot's range in the shared buffers. */
export interface MeshElementGeometry {
  readonly texture: string;
  readonly visibility: readonly number[];
  readonly frames: readonly number[];
  /** `[firstVertex, firstIndex, indexCount]` per slot. */
  readonly geometry: readonly (readonly number[])[];
}

export interface MeshGeometry {
  readonly frameCount: number;
  readonly elements: readonly MeshElementGeometry[];
}

/** One `Group MeshElement` of a `.mse`, as `CEffectMeshScript` reads it. */
export interface MeshScriptElement {
  readonly billboard: number;
  readonly blend: boolean;
  readonly srcBlend: number;
  readonly dstBlend: number;
  readonly textureLoop: boolean;
  readonly textureDelayS: number;
  readonly textureStartFrame: number;
  readonly colourOp: number;
  readonly colourFactor: readonly number[];
  readonly alpha: Curve;
}

/** One `Group Mesh` of a `.mse`, and the `.mde` it names. */
export interface MeshScript {
  readonly mesh: string;
  readonly startS: number;
  readonly offsetMm: readonly number[];
  readonly loop: boolean;
  readonly loopCount: number;
  readonly frameDelayS: number;
  readonly elements: readonly MeshScriptElement[];
}

/** The manifest fields the mesh half reads. Optional throughout: a pack built
 *  before the mesh element existed carries none of them, and then this layer
 *  draws nothing rather than failing the whole effect layer. */
export interface MeshManifest {
  readonly meshAtlas?: { readonly image: string; readonly width: number; readonly height: number };
  readonly meshTextures?: Readonly<Record<string, readonly number[]>>;
  readonly meshBinary?: { readonly file: string; readonly vertexFloats: number; readonly indexCount: number };
  readonly meshes?: Readonly<Record<string, MeshGeometry>>;
}

/** The four `EMeshBillBoardType` values (`EffectLib/Type.h:54-62`). */
export const MESH_BILLBOARD_NONE = 0;
export const MESH_BILLBOARD_ALL = 1;
export const MESH_BILLBOARD_Y = 2;
export const MESH_BILLBOARD_MOVE = 3;

/** How much of the colour factor reaches the pixel, by `D3DTSS_COLOROP`.
 *  Stage 0 is `ARG1 = D3DTA_TFACTOR`, `ARG2 = D3DTA_TEXTURE`
 *  (`EffectInstance.cpp:104-105`), so `SELECTARG2` is the texture alone and
 *  the three `MODULATE` steps multiply it by the factor and then by 1, 2 or 4
 *  (`d3d9types.h`'s `D3DTOP_MODULATE`, `MODULATE2X`, `MODULATE4X` = 4, 5, 6).
 */
const COLOUR_OP_SCALE: ReadonlyMap<number, number> = new Map([
  [3, 0],
  [4, 1],
  [5, 2],
  [6, 4],
]);

/** `CFrameController` (`EffectLib/FrameController.cpp:4-103`): a frame index
 *  stepped by a fixed delay, that stops or restarts when it runs off the end.
 */
export class FrameClock {
  frame = 0;
  active = true;
  /** `m_fLastFrameTime`, which `SetFrameTime` primes to one whole delay
   *  (`FrameController.cpp:53-57`, after `Clear` zeroed it at `:90`). */
  private leftS: number;
  private loopsLeft: number;

  constructor(
    readonly maxFrame: number,
    readonly frameTimeS: number,
    readonly loop: boolean,
    /** Null where nothing ever calls `SetLoopCount`, so the count cannot
     *  reach zero and a looping clock runs for as long as it is stepped --
     *  the texture controllers, which `OnSetDataPointer` sets max frame,
     *  frame time, loop flag and start frame on and nothing else
     *  (`EffectMeshInstance.cpp:220-225`). */
    loopCount: number | null,
    readonly startFrame: number,
  ) {
    this.leftS = frameTimeS;
    this.loopsLeft = loopCount ?? 0;
    this.loop = loop;
  }

  /** `isActive(dwMainFrame)` (`FrameController.cpp:78-84`): a clock whose
   *  start frame the driving frame has not reached yet is not active. */
  isActive(mainFrame = 0): boolean {
    return mainFrame < this.startFrame ? false : this.active;
  }

  /** `Update` (`FrameController.cpp:4-37`), including its cap of twenty
   *  frames per call: a step longer than twenty delays loses the rest. */
  step(dtS: number): void {
    this.leftS -= dtS;
    for (let i = 0; i < 20; i += 1) {
      if (this.leftS >= 0) return;
      this.leftS += this.frameTimeS;
      this.frame += 1;
      if (this.frame < this.maxFrame) continue;
      // `--m_iLoopCount != 0`: a count of zero decrements to -1, which is not
      // zero, and is then clamped back to zero -- so zero means forever.
      this.loopsLeft -= 1;
      if (this.loop && this.loopsLeft !== 0) {
        if (this.loopsLeft < 0) this.loopsLeft = 0;
        this.frame = 0;
        continue;
      }
      this.loopsLeft = 1;
      this.frame = 0;
      this.active = false;
      return;
    }
  }
}

/** `D3DXCOLOR`'s conversion to a packed DWORD, which is the only place the
 *  original bounds a colour channel. */
function clamp01(value: number): number {
  return value <= 0 ? 0 : value >= 1 ? 1 : value;
}

/** No entity: a mesh that stands where it was fired. `particles.ts` uses the
 *  same sentinel for the same reason -- 0 is a real entity id on this wire. */
const NO_FOLLOW = -1;

interface LiveMesh {
  readonly script: MeshScript;
  readonly geometry: MeshGeometry;
  /** `m_fRemainingTime`/`m_bStart` (`EffectElementBaseInstance.cpp:4-19`). */
  startLeftS: number;
  started: boolean;
  /** `m_fLocalTime`, which only runs once the element has started. */
  localS: number;
  readonly mesh: FrameClock;
  readonly textures: readonly FrameClock[];
  originX: number;
  originY: number;
  originZ: number;
  readonly followId: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly offsetZ: number;
  readonly cos: number;
  readonly sin: number;
  /** Replaced by the axes a followed anchor turns the instance to. */
  basis: ArrayLike<number> | null;
  readonly scale: number;
  /** `SpawnOptions.instance`. */
  readonly instance: number;
}

/** One sub-mesh to draw this frame, already resolved to a world placement. */
export interface MeshDraw {
  readonly texture: string;
  readonly firstIndex: number;
  readonly indexCount: number;
  readonly billboard: number;
  readonly blend: boolean;
  readonly srcBlend: number;
  readonly dstBlend: number;
  /** Premultiplied by the colour operation's own 1x/2x/4x. */
  readonly colour: readonly [number, number, number, number];
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly offsetMm: readonly number[];
  readonly cos: number;
  readonly sin: number;
  /** A placed effect's local-to-world axes, column-major 3x3; replaces the
   *  heading when given. See `SpawnOptions.basis`. */
  readonly basis?: ArrayLike<number> | null;
  readonly followId?: number;
  readonly scale: number;
}

/** Live mesh instances, the half of `CEffectMeshInstance` with no device in
 *  it: `spawn` starts one, `step` runs its two frame clocks, and `visible`
 *  says what `OnRender` would draw. */
export class EffectMeshField {
  private readonly live: LiveMesh[] = [];
  private readonly followed = new Map<number, {
    xMm: number; yMm: number; zMm: number; basis: ArrayLike<number> | null;
  }>();

  constructor(
    private readonly manifest: EffectManifest & MeshManifest,
    /** The most mesh instances that may be alive at once. A cast puts at most
     *  one mesh group on screen and the level-up burst one more, so this is
     *  the same order as `MAX_EMITTERS` and is here to bound a leak, not to
     *  ration anything a player can reach. */
    private readonly capacity = 64,
  ) {}

  get count(): number {
    return this.live.length;
  }

  /** Whether this effect carries a mesh element this pack converted. */
  has(key: string): boolean {
    return this.scriptsFor(key).length > 0;
  }

  private scriptsFor(key: string): readonly MeshScript[] {
    const effect = this.manifest.effects[key] as { meshes?: readonly MeshScript[] } | undefined;
    const scripts = effect?.meshes;
    if (scripts === undefined || this.manifest.meshes === undefined) return [];
    return scripts.filter((script) => script.mesh in this.manifest.meshes!);
  }

  spawn(key: string, options: SpawnOptions): number {
    const scripts = this.scriptsFor(key);
    if (scripts.length === 0) return 0;
    const heading = options.headingRad ?? 0;
    const followId = options.followId ?? NO_FOLLOW;
    const anchor = followId === NO_FOLLOW ? undefined : this.followed.get(followId);
    let added = 0;
    for (const script of scripts) {
      while (this.live.length >= this.capacity) this.live.shift();
      const geometry = this.manifest.meshes![script.mesh];
      this.live.push({
        script,
        geometry,
        startLeftS: script.startS,
        started: script.startS <= 0,
        localS: 0,
        mesh: new FrameClock(
          geometry.frameCount, script.frameDelayS, script.loop, script.loopCount, 0),
        textures: geometry.elements.map((_element, index) => {
          const scripted = script.elements[index];
          return new FrameClock(
            // One image per sub-mesh: every diffuse map in the converted set
            // is a single file, never the `.ifl` list `EffectMesh.cpp:193-223`
            // would expand, so `SetMaxFrame(rTextureVector.size())` is one.
            1,
            scripted.textureDelayS,
            scripted.textureLoop,
            null,
            scripted.textureStartFrame,
          );
        }),
        originX: options.xMm,
        originY: options.yMm,
        originZ: options.zMm,
        followId,
        offsetX: anchor === undefined ? 0 : options.xMm - anchor.xMm,
        offsetY: anchor === undefined ? 0 : options.yMm - anchor.yMm,
        offsetZ: anchor === undefined ? 0 : options.zMm - anchor.zMm,
        cos: Math.cos(heading),
        sin: Math.sin(heading),
        basis: options.basis ?? null,
        scale: options.scale ?? 1,
        instance: options.instance ?? 0,
      });
      added += 1;
    }
    return added;
  }

  /** Move a body its meshes hang on, and turn them with it when `basis`
   *  (column-major 3x3) is given (`ActorInstanceAttach.cpp:553-563`). */
  follow(entityId: number, xMm: number, yMm: number, zMm: number, basis: ArrayLike<number> | null = null): void {
    const row = this.followed.get(entityId);
    if (row === undefined) this.followed.set(entityId, { xMm, yMm, zMm, basis });
    else {
      row.xMm = xMm;
      row.yMm = yMm;
      row.zMm = zMm;
      row.basis = basis;
    }
  }


  unfollow(entityId: number): void {
    this.followed.delete(entityId);
    for (let i = this.live.length - 1; i >= 0; i -= 1) {
      if (this.live[i].followId === entityId) this.live.splice(i, 1);
    }
  }

  stopFollowing(entityId: number, key: string): void {
    const scripts = new Set(this.scriptsFor(key));
    for (let i = this.live.length - 1; i >= 0; i -= 1) {
      const one = this.live[i];
      if (one.followId === entityId && scripts.has(one.script)) this.live.splice(i, 1);
    }
  }

  /** A deactivated mesh element is no longer active, so it is neither drawn
   *  nor updated again (`EffectMeshInstance.cpp:25-28,45-46`). */
  deactivate(instance: number): void {
    if (instance === 0) return;
    for (let i = this.live.length - 1; i >= 0; i -= 1) {
      if (this.live[i].instance === instance) this.live.splice(i, 1);
    }
  }

  isFollowing(entityId: number, key: string): boolean {
    const scripts = new Set(this.scriptsFor(key));
    return this.live.some((one) => one.followId === entityId && scripts.has(one.script));
  }

  clear(): void {
    this.live.length = 0;
    this.followed.clear();
  }

  /** `CEffectMeshInstance::isActive` (`EffectMeshInstance.cpp:25-41`): the
   *  mesh clock still running, and at least one texture clock active at the
   *  frame the mesh clock is on. */
  private alive(one: LiveMesh): boolean {
    if (!one.mesh.isActive()) return false;
    return one.textures.some((clock) => clock.isActive(one.mesh.frame));
  }

  step(dtS: number): void {
    for (let i = this.live.length - 1; i >= 0; i -= 1) {
      const one = this.live[i];
      if (!one.started) {
        one.startLeftS -= dtS;
        if (one.startLeftS <= 0) one.started = true;
        continue;
      }
      one.localS += dtS;
      if (this.alive(one)) {
        one.mesh.step(dtS);
        for (const clock of one.textures) {
          if (clock.isActive(one.mesh.frame)) clock.step(dtS);
        }
      }
      if (!this.alive(one)) this.live.splice(i, 1);
      else this.reanchor(one);
    }
  }

  /** The moving body times the fixed local offset, the mesh half of
   *  `UpdateAttachingInstances`'s product (`ActorInstanceAttach.cpp:553-563`). */
  private reanchor(one: LiveMesh): void {
    if (one.followId === NO_FOLLOW) return;
    const anchor = this.followed.get(one.followId);
    if (anchor === undefined) return;
    one.originX = anchor.xMm + one.offsetX;
    one.originY = anchor.yMm + one.offsetY;
    one.originZ = anchor.zMm + one.offsetZ;
    if (anchor.basis !== null) one.basis = anchor.basis;
  }

  /** Every sub-mesh `OnRender` would draw this frame, in the order it draws
   *  them (`EffectMeshInstance.cpp:61-177`). `shown`, when given, is asked of
   *  each instance's follow id, and an instance it refuses is not drawn. */
  visible(shown?: (followId: number) => boolean): MeshDraw[] {
    const out: MeshDraw[] = [];
    for (const one of this.live) {
      if (!one.started || !this.alive(one)) continue;
      if (shown !== undefined && !shown(one.followId)) continue;
      const elements = one.geometry.elements;
      for (let i = 0; i < elements.length; i += 1) {
        const clock = one.textures[i];
        if (!clock.isActive(one.mesh.frame)) continue;
        const element = elements[i];
        if (element.texture === "") continue;
        const scripted = one.script.elements[i];
        const frame = Math.min(one.mesh.frame, element.frames.length - 1);
        const slot = element.frames[frame];
        const range = element.geometry[slot];
        const scale = COLOUR_OP_SCALE.get(scripted.colourOp) ?? 1;
        const alpha = scripted.alpha.length === 0
          ? 1
          : sampleCurve(scripted.alpha, one.localS);
        out.push({
          texture: element.texture,
          firstIndex: range[1],
          indexCount: range[2],
          billboard: scripted.billboard,
          blend: scripted.blend,
          srcBlend: scripted.srcBlend,
          dstBlend: scripted.dstBlend,
          colour: [
            scale === 0 ? 1 : clamp01(scripted.colourFactor[0]) * scale,
            scale === 0 ? 1 : clamp01(scripted.colourFactor[1]) * scale,
            scale === 0 ? 1 : clamp01(scripted.colourFactor[2]) * scale,
            // `Color.a = fAlpha * rFrameData.fVisibility` (`:169`), packed
            // into `D3DRS_TEXTUREFACTOR` as a DWORD, which is where the
            // clamp is: `gyeoksantau.mde` writes 9.533334 in every one of its
            // first sub-mesh's 31 frames, and the original draws it opaque.
            clamp01(alpha * element.visibility[frame]),
          ],
          xMm: one.originX,
          yMm: one.originY,
          zMm: one.originZ,
          offsetMm: one.script.offsetMm,
          cos: one.cos,
          sin: one.sin,
          basis: one.basis,
          followId: one.followId,
          scale: one.scale,
        });
      }
    }
    return out;
  }
}

// ------------------------------------------------------------------ the model

/** The rotation `OnRender` builds for one billboard type, written into the
 *  first nine slots of a column-major 4x4 (`EffectMeshInstance.cpp:81-121`).
 *  `camera` is the camera's own world basis, the inverse of the view matrix
 *  the original takes there; `MESH_BILLBOARD_MOVE` is the identity for every
 *  mesh group this pack converts, all 33 of which carry one constant
 *  `TimeEventPosition` row and so never move against their own last position.
 */
export function billboardRotation(
  type: number,
  camera: { right: readonly number[]; up: readonly number[]; forward: readonly number[] },
  out: Float32Array,
): Float32Array {
  out.fill(0);
  out[0] = out[5] = out[10] = out[15] = 1;
  if (type === MESH_BILLBOARD_ALL) {
    // `D3DXMatrixRotationX(&matTemp, 90.0f)`: D3DX takes radians, so the
    // original's 90 is 90 radians, not a right angle. Kept, because it is
    // what the two gathering-spirit sub-meshes are drawn with.
    const c = Math.cos(90);
    const s = Math.sin(90);
    // `matTemp * inverse(view)` in the original's row-vector convention, so
    // row i is `rows[i]` against the camera's own basis; the transpose lands
    // in this column-major array because GL multiplies the other way round.
    const rows: readonly (readonly number[])[] = [
      [1, 0, 0],
      [0, c, s],
      [0, -s, c],
    ];
    const basis = [camera.right, camera.up, camera.forward];
    for (let row = 0; row < 3; row += 1) {
      for (let component = 0; component < 3; component += 1) {
        out[row * 4 + component] = rows[row][0] * basis[0][component]
          + rows[row][1] * basis[1][component]
          + rows[row][2] * basis[2][component];
      }
    }
    return out;
  }
  if (type === MESH_BILLBOARD_Y) {
    // Only the upper-left 2x2 is copied out of the inverse view matrix
    // (`EffectMeshInstance.cpp:99-102`); the rest of the identity stands.
    out[0] = camera.right[0];
    out[4] = camera.right[1];
    out[1] = camera.up[0];
    out[5] = camera.up[1];
  }
  return out;
}

/** `m_matWorld * *mc_pmatLocal` (`EffectMeshInstance.cpp:136-142`) as one
 *  column-major matrix: the billboard rotation, then the element's own
 *  constant offset, then the effect's placement -- heading, scale, origin. */
export function meshModelMatrix(
  draw: MeshDraw,
  camera: { right: readonly number[]; up: readonly number[]; forward: readonly number[] },
  out: Float32Array,
): Float32Array {
  const rotation = billboardRotation(draw.billboard, camera, out);
  const b = draw.basis ?? null;
  // Fold the heading, or a placement's whole basis, and the scale into the
  // rotation's three columns, so the whole thing stays one 4x4 rather than a
  // product built every draw.
  for (let column = 0; column < 3; column += 1) {
    const x = rotation[column * 4];
    const y = rotation[column * 4 + 1];
    const z = rotation[column * 4 + 2];
    if (b === null) {
      rotation[column * 4] = (x * draw.cos - y * draw.sin) * draw.scale;
      rotation[column * 4 + 1] = (x * draw.sin + y * draw.cos) * draw.scale;
      rotation[column * 4 + 2] = z * draw.scale;
    } else {
      rotation[column * 4] = (b[0] * x + b[3] * y + b[6] * z) * draw.scale;
      rotation[column * 4 + 1] = (b[1] * x + b[4] * y + b[7] * z) * draw.scale;
      rotation[column * 4 + 2] = (b[2] * x + b[5] * y + b[8] * z) * draw.scale;
    }
  }
  const [ox, oy, oz] = draw.offsetMm;
  if (b === null) {
    out[12] = draw.xMm + (ox * draw.cos - oy * draw.sin) * draw.scale;
    out[13] = draw.yMm + (ox * draw.sin + oy * draw.cos) * draw.scale;
    out[14] = draw.zMm + oz * draw.scale;
  } else {
    out[12] = draw.xMm + (b[0] * ox + b[3] * oy + b[6] * oz) * draw.scale;
    out[13] = draw.yMm + (b[1] * ox + b[4] * oy + b[7] * oz) * draw.scale;
    out[14] = draw.zMm + (b[2] * ox + b[5] * oy + b[8] * oz) * draw.scale;
  }
  out[15] = 1;
  return out;
}

// --------------------------------------------------------------------- the GL

/** `D3DBLEND` to its WebGL factor. The five the converted `.mse` files use,
 *  plus the two that complete the pair (`d3d9types.h`'s `D3DBLEND_ZERO` = 1
 *  through `D3DBLEND_INVSRCALPHA` = 6). */
export function blendFactor(gl: WebGL2RenderingContext, d3d: number): number {
  switch (d3d) {
    case 1: return gl.ZERO;
    case 2: return gl.ONE;
    case 3: return gl.SRC_COLOR;
    case 4: return gl.ONE_MINUS_SRC_COLOR;
    case 5: return gl.SRC_ALPHA;
    case 6: return gl.ONE_MINUS_SRC_ALPHA;
    default: return gl.ONE;
  }
}

const VERTEX_SOURCE = `#version 300 es
precision highp float;

in vec3 a_positionMm;
in vec2 a_uv;

uniform mat4 u_viewProj;
uniform mat4 u_model;

out vec2 v_uv;

void main() {
  gl_Position = u_viewProj * u_model * vec4(a_positionMm, 1.0);
  v_uv = a_uv;
}
`;

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec2 v_uv;

uniform sampler2D u_atlas;
uniform vec4 u_cell;
uniform vec4 u_colour;

out vec4 fragColour;

void main() {
  // Seven of the 66 converted sub-meshes carry coordinates outside 0..1 --
  // \`arrow_01.mde\` runs to +-5.77 -- which the original draws with the
  // device's own D3DTADDRESS_WRAP. The atlas is sampled at point size
  // (\`EffectInstance.cpp:94-95\` sets D3DTEXF_NONE), so wrapping into the cell
  // here is the same texel that a wrapping sampler would have picked.
  vec2 wrapped = fract(v_uv);
  vec4 texel = texture(u_atlas, u_cell.xy + wrapped * u_cell.zw);
  // Stage 0 modulates the texture by the texture factor, alpha always and
  // colour unless the element asked for SELECTARG2 -- which the caller sends
  // as a white factor (\`EffectInstance.cpp:104-109\`, \`EffectMeshInstance.cpp:147\`).
  fragColour = vec4(texel.rgb * u_colour.rgb, texel.a * u_colour.a);
}
`;

const UNIFORMS = ["u_viewProj", "u_model", "u_atlas", "u_cell", "u_colour"] as const;

/** The device half: one static vertex and index buffer for every `.mde` in
 *  the pack, and one draw call per sub-mesh the field says is visible. */
export class EffectMeshLayer {
  private program: WebGLProgram | null = null;
  private uniforms: Uniforms<(typeof UNIFORMS)[number]> | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private atlas: WebGLTexture | null = null;
  private readonly cells = new Map<string, Float32Array>();
  private readonly model = new Float32Array(16);

  constructor(private readonly gl: WebGL2RenderingContext) {}

  get ready(): boolean {
    return this.program !== null && this.vao !== null && this.atlas !== null;
  }

  /** Adopt the geometry blob and the mesh atlas. `binary` is `meshes.bin`:
   *  `vertexFloats` floats of interleaved position and texture coordinate,
   *  then `indexCount` 16-bit indices local to their own frame. */
  install(
    manifest: EffectManifest & MeshManifest,
    binary: ArrayBuffer,
    image: ImageBitmap | HTMLImageElement,
  ): void {
    const gl = this.gl;
    const layout = manifest.meshBinary;
    const atlas = manifest.meshAtlas;
    const textures = manifest.meshTextures;
    if (layout === undefined || atlas === undefined || textures === undefined) return;
    if (binary.byteLength !== layout.vertexFloats * 4 + layout.indexCount * 2) {
      throw new GlError(
        `${layout.file} is ${binary.byteLength} bytes, not the ${layout.vertexFloats} floats `
        + `and ${layout.indexCount} indices the manifest names`,
      );
    }
    this.program = createProgram(gl, VERTEX_SOURCE, FRAGMENT_SOURCE, "effect meshes");
    this.uniforms = uniformLocations(gl, this.program, UNIFORMS);

    this.vao = createVertexArray(gl, "effect meshes");
    gl.bindVertexArray(this.vao);
    const vertices = createBuffer(gl, "effect mesh vertices");
    gl.bindBuffer(gl.ARRAY_BUFFER, vertices);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(binary, 0, layout.vertexFloats), gl.STATIC_DRAW);
    describeAttributes(gl, this.program, 20, [
      { name: "a_positionMm", components: 3, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
      { name: "a_uv", components: 2, type: gl.FLOAT, normalized: false, offset: 12, divisor: 0 },
    ]);
    const indices = createBuffer(gl, "effect mesh indices");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indices);
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      biasedIndices(manifest, new Uint16Array(binary, layout.vertexFloats * 4, layout.indexCount)),
      gl.STATIC_DRAW,
    );
    gl.bindVertexArray(null);

    const texture = gl.createTexture();
    if (texture === null) throw new GlError("could not create the effect mesh atlas texture");
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    // Point sampling, both because the original draws effects with
    // `D3DTEXF_NONE` and because it is what makes `fract` in the shader above
    // pick the same texel a wrapping sampler would.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.atlas = texture;

    this.cells.clear();
    for (const [key, rect] of Object.entries(textures)) {
      this.cells.set(key, new Float32Array([
        rect[0] / atlas.width,
        rect[1] / atlas.height,
        rect[2] / atlas.width,
        rect[3] / atlas.height,
      ]));
    }
  }

  /** Draw the sub-meshes `field.visible()` returned, after the particles and
   *  in the order they were given (`EffectInstance.cpp:112-113` renders the
   *  mesh instances after the particle instances). */
  draw(draws: readonly MeshDraw[], camera: Camera, basis: {
    right: readonly number[]; up: readonly number[]; forward: readonly number[];
  }, depthTested = false): number {
    const gl = this.gl;
    const program = this.program;
    const uniforms = this.uniforms;
    if (program === null || uniforms === null || this.vao === null || this.atlas === null) return 0;
    if (draws.length === 0) return 0;

    gl.useProgram(program);
    gl.bindVertexArray(this.vao);
    gl.uniformMatrix4fv(uniforms.u_viewProj, false, camera.viewProj);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.atlas);
    gl.uniform1i(uniforms.u_atlas, 0);
    // `D3DRS_ZWRITEENABLE FALSE` (`EffectInstance.cpp:101`): a mesh element
    // never occludes what is drawn after it. Its `D3DCULL_NONE` neighbour
    // needs nothing here, face culling being off by default in WebGL and
    // never enabled anywhere in this client.
    gl.depthMask(false);
    // A map's own effects keep the depth test the world pass left on
    // (`MapOutdoorRender.cpp:338-349` inside `BeginEnvironment`), so a glint
    // set into a wall is hidden by the wall in front of it.
    if (depthTested) gl.enable(gl.DEPTH_TEST);
    else gl.disable(gl.DEPTH_TEST);

    let drawn = 0;
    for (const one of draws) {
      const cell = this.cells.get(one.texture);
      if (cell === undefined) continue;
      if (one.blend) {
        gl.enable(gl.BLEND);
        gl.blendFunc(blendFactor(gl, one.srcBlend), blendFactor(gl, one.dstBlend));
      } else {
        gl.disable(gl.BLEND);
      }
      gl.uniformMatrix4fv(uniforms.u_model, false, meshModelMatrix(one, basis, this.model));
      gl.uniform4fv(uniforms.u_cell, cell);
      gl.uniform4f(uniforms.u_colour, one.colour[0], one.colour[1], one.colour[2], one.colour[3]);
      gl.drawElements(gl.TRIANGLES, one.indexCount, gl.UNSIGNED_INT, one.firstIndex * 4);
      drawn += 1;
    }

    gl.disable(gl.BLEND);
    gl.depthMask(true);
    gl.bindVertexArray(null);
    return drawn;
  }
}

/** The packed 16-bit indices, widened to 32 bits and biased by each frame's
 *  own first vertex, so every `.mde` in the pack draws out of one buffer with
 *  one attribute binding rather than one per sub-mesh. */
export function biasedIndices(manifest: MeshManifest, packed: Uint16Array): Uint32Array {
  const out = new Uint32Array(packed.length);
  for (const mesh of Object.values(manifest.meshes ?? {})) {
    for (const element of mesh.elements) {
      for (const [firstVertex, firstIndex, indexCount] of element.geometry) {
        for (let i = 0; i < indexCount; i += 1) {
          out[firstIndex + i] = packed[firstIndex + i] + firstVertex;
        }
      }
    }
  }
  return out;
}
